export function buildMonthOptions(periods, currentMonth) {
  const options = [...(periods || [])].sort((a, b) => b.value.localeCompare(a.value));
  const selected = options.some((item) => item.value === currentMonth)
    ? currentMonth
    : (options[0]?.value || currentMonth);
  return { options, selected };
}

export function sparklinePoints(values, width = 160, height = 48) {
  if (!values?.length) return '';
  const numeric = values.map((value) => Number(value) || 0);
  const minimum = Math.min(...numeric);
  const maximum = Math.max(...numeric);
  const range = maximum - minimum;
  return numeric.map((value, index) => {
    const x = numeric.length === 1 ? width / 2 : index / (numeric.length - 1) * width;
    const y = range === 0 ? height / 2 : height - ((value - minimum) / range * height);
    return `${Number(x.toFixed(2))},${Number(y.toFixed(2))}`;
  }).join(' ');
}

export function deltaTone(direction, inverse = false) {
  if (direction === 'flat' || !direction) return 'neutral';
  const positive = direction === 'up';
  return positive !== inverse ? 'positive' : 'negative';
}

export function financialCardDefinitions() {
  return [
    { key: 'projected_student_charges', label: 'Projected Student Charges' },
    { key: 'realized_student_charges', label: 'Realized Student Charges' },
    { key: 'cash_collected', label: 'Cash Collected' },
    { key: 'accrued_teacher_pay', label: 'Accrued Teacher Pay', neutral: true },
    { key: 'realized_contribution', label: 'Realized Gross Profit' },
    { key: 'total_profit', label: 'Total Profit' },
  ];
}

export function cashFlowCardDefinitions() {
  return [
    { key: 'total_bank_outflows', label: 'Bank Outflows (excl. prior-month teacher pay)', inverse: true },
    { key: 'company_expenditures', label: 'Company Expenditures', inverse: true },
    { key: 'net_cash_flow', label: 'Net Cash Flow (excl. prior-month teacher pay)' },
  ];
}

export function formatCurrency(value) {
  return new Intl.NumberFormat('en-IE', {
    style: 'currency', currency: 'EUR', maximumFractionDigits: 2,
  }).format(Number(value) || 0);
}

export function formatOptionalCurrency(value) {
  return value == null ? '—' : formatCurrency(value);
}

export function formatDelta(comparison) {
  if (!comparison) return 'No comparison';
  const sign = Number(comparison.absolute_change) > 0 ? '+' : '';
  const percentage = comparison.percentage_change == null
    ? 'new'
    : `${comparison.percentage_change > 0 ? '+' : ''}${comparison.percentage_change}%`;
  return `${sign}${formatCurrency(comparison.absolute_change)} · ${percentage}`;
}

function comparisonDate(value) {
  if (!value) return 'unavailable';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'unavailable';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Vilnius', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  return `${values.year}-${values.month}-${values.day} ${values.hour}:${values.minute}:${values.second}`;
}

export function financialComparisonRows(metric = {}) {
  return [
    ['comparison', 'Previous month'],
    ['year_over_year_comparison', 'Same month last year'],
  ].map(([key, name]) => {
    const comparison = metric?.[key];
    const month = comparison?.period?.month;
    const monthDate = /^\d{4}-(0[1-9]|1[0-2])$/.test(month || '') ? new Date(`${month}-01T00:00:00Z`) : null;
    const monthLabel = monthDate ? new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(monthDate) : null;
    const label = monthLabel ? `vs ${monthLabel} · ${name}` : name;
    const validNumbers = comparison?.previous_value != null && comparison?.absolute_change != null
      && Number.isFinite(Number(comparison.previous_value)) && Number.isFinite(Number(comparison.absolute_change));
    const available = comparison?.status === 'available' && validNumbers;
    let value = 'Comparison unavailable';
    if (comparison?.status === 'partial') value = 'Comparison incomplete';
    else if (comparison?.status === 'unavailable') value = key === 'year_over_year_comparison' ? 'No recorded prior-year data' : 'No recorded previous-month data';
    else if (available) {
      const change = Number(comparison.absolute_change);
      const classCount = metric?.unit === 'classes';
      const percent = comparison.percentage_change == null
        ? `Percentage unavailable—baseline ${classCount ? '0' : '€0'}`
        : `${comparison.percentage_change > 0 ? '+' : ''}${comparison.percentage_change}%`;
      value = `${change > 0 ? '+' : ''}${classCount ? new Intl.NumberFormat('en-IE').format(change) : formatCurrency(change)} · ${percent}`;
    }
    const range = comparison?.range_basis === 'full_month_projection' ? 'Full-month projection'
      : comparison?.range_basis === 'full_month_schedule' ? 'Full-month schedules'
      : comparison?.range_basis === 'full_month' ? 'Full months' : 'Matching elapsed dates';
    const basis = comparison?.calculation_basis === 'recalculated'
      ? 'Recalculated using the dashboard pricing and account data; not a finalized historical payment total.'
      : metric?.unit === 'classes' ? 'Based on recorded class schedules and feedback.' : 'Based on recorded transactions.';
    const warnings = (comparison?.warnings || []).map((warning) => `${warning.period || ''}: ${String(warning.code || 'incomplete data').replaceAll('_', ' ')}`).join('; ');
    const details = comparison
      ? `${range}. Current: ${comparisonDate(comparison.current_period?.start)} until ${comparisonDate(comparison.current_data_through)}. Comparison: ${comparisonDate(comparison.period?.start)} until ${comparisonDate(comparison.data_through)}. Cutoffs are exclusive; Europe/Vilnius. ${basis}${comparison.status === 'unavailable' ? ' No relevant source records were found; this does not establish zero activity.' : ''}${warnings ? ` Data quality: ${warnings}.` : ''}`
      : 'This snapshot does not contain this comparison. Refresh data to calculate it.';
    return { key, label, value, details, direction: available ? comparison.direction : null };
  });
}

export function volumeBarTooltip(point, series) {
  if (series === 'completed') {
    return `${point.date} · Teacher-confirmed completed: ${Number(point.completed_classes) || 0}`;
  }
  return `${point.date} · Active scheduled: ${Number(point.active_classes) || 0}`;
}

export function cumulativeChartPoints(rows, key, width = 800, height = 210) {
  if (!rows?.length) return [];
  const values = rows.map((row) => Number(row[key]) || 0);
  const minimum = Math.min(...values);
  const maximum = Math.max(...values);
  const range = maximum - minimum;

  return rows.map((row, index) => {
    const value = values[index];
    const x = rows.length === 1 ? width / 2 : index / (rows.length - 1) * width;
    const y = range === 0 ? height / 2 : height - ((value - minimum) / range * height);
    return {
      x: Number(x.toFixed(2)),
      y: Number(y.toFixed(2)),
      date: row.date,
      value,
      dailyChange: value - (index === 0 ? 0 : values[index - 1]),
    };
  });
}

// Daily series contain cumulative money values; subtract before explaining a day.
export function chartDayDetails(data, key, index, teacher = null) {
  const rows = teacher?.daily_series || data?.series?.daily || [];
  const row = rows[index] || {};
  const previous = rows[index - 1];
  const number = value => value == null || !Number.isFinite(Number(value)) ? null : Number(value);
  const delta = field => {
    const current = number(row[field]);
    const before = index === 0 ? 0 : number(previous?.[field]);
    return current == null || before == null ? null : Math.round((current - before) * 100) / 100;
  };
  const component = (label, field, sign = 1) => ({label, amount: delta(field) == null ? null : delta(field) * sign});
  const specs = {
    net_cash_flow: [['Cash collected','cash_collected',1],['Bank outflows','total_bank_outflows',-1]],
    realized_contribution: [['Realized student charges','realized_student_charges',1],['Accrued teacher pay','accrued_teacher_pay',-1]],
    total_profit: [['Realized student charges','realized_student_charges',1],['Accrued teacher pay','accrued_teacher_pay',-1],['Other company expenses','non_teacher_expenditures',-1]],
  };
  const components = (specs[key] || []).map(args => component(...args));
  const localDate = new Intl.DateTimeFormat('en-CA',{timeZone:'Europe/Vilnius',year:'numeric',month:'2-digit',day:'2-digit'});
  const cutoff = data?.data_through ? localDate.format(new Date(data.data_through)) : rows.at(-1)?.date;
  const start = data?.series?.daily?.[0]?.date;
  const transactions = (data?.expenditures?.transactions || []).filter(transaction => {
    if (key === 'net_cash_flow' || key === 'total_bank_outflows') return transaction.date === row.date && transaction.included_in_cash_flow === true;
    if (!['company_expenditures','total_profit'].includes(key)) return false;
    if (!transaction.is_company_expenditure || !transaction.expense_in_period) return false;
    if (key === 'total_profit' && transaction.category === 'Teacher Compensation') return false;
    let date = transaction.expense_date || transaction.date;
    const plottedCutoff = cutoff < start ? start : cutoff;
    if (date > plottedCutoff) date = plottedCutoff;
    return date === row.date;
  }).sort((a,b) => Number(b.amount) - Number(a.amount));
  const teachers = ['accrued_teacher_pay','realized_contribution','total_profit'].includes(key)
    ? (data?.teacher_watchlist || []).map(owner => {
      const points = owner.daily_series || [];
      const dayIndex = points.findIndex(point => point.date === row.date);
      const amount = dayIndex < 0 ? 0 : Number(points[dayIndex].accrued_pay || 0) - Number(points[dayIndex - 1]?.accrued_pay || 0);
      return {label:owner.teacher_name,amount:Math.round(amount * 100) / 100};
    }).filter(owner => owner.amount !== 0).sort((a,b)=> b.amount-a.amount) : [];
  let note = '';
  if (key === 'projected_student_charges') note = 'Scheduled lesson prices for this date, including past and future lessons. Individual class quotes are not included in this snapshot.';
  if (key === 'realized_student_charges') note = 'Recorded student charges assigned to the lesson date. Individual charge records are not included in this snapshot.';
  if (key === 'cash_collected' || key === 'net_cash_flow') note = 'Cash collected is the daily settled-payment total. Individual incoming payments are not included in this snapshot.';
  if (key === 'net_cash_flow' || key === 'total_bank_outflows') note += ' Outflows exclude prior-month teacher pay, matching this graph.';
  if (key === 'company_expenditures') note = 'Expenses follow their allocated service date. Month-level allocations appear at the reporting cutoff; payment dates are shown below.';
  if (key === 'total_profit') note = 'Teacher cost is deducted once through accrued pay. Payment rows below contain only other company expenses.';
  if (teacher) note = 'Daily accrued pay for this teacher, with feedback and completed-class totals through this date.';
  return {date:row.date,value:number(row[key]),change:delta(key),components,transactions,teachers,note,
    counts: teacher ? [{label:'Completed through this date',value:row.completed_classes},{label:'Feedback submitted / due',value:`${row.feedback_submitted || 0} / ${row.feedback_due || 0}`}]
      : [{label:'Active scheduled lessons',value:row.active_classes},{label:'Teacher-confirmed completed',value:row.completed_classes}].filter(item=>item.value != null)};
}
